import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  CreditCard,
  DollarSign,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Phone,
  MapPin,
  Save,
  BookOpen
} from 'lucide-react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { createUserProfile } from '../../firebase/collections';
import { paymentManagementService } from '../../firebase/paymentManagement';
import { getSubscriptionPlans, CATEGORIES, STUDENT_RANGES, getBaseBatchLimit, BATCH_ADDON_CONFIG } from '../../utils/subscriptionPricing';
import toast from 'react-hot-toast';

const InstituteAccountCreation = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Institute Details
    coachingName: '',
    coachingId: '',
    contactPerson: '',
    email: '',
    phone: '',
    password: '',
    
    // Address
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    
    // Category and Student Range
    category: '',
    studentRange: '',
    
    // Subscription Details
    subscriptionPlan: 'yearly',
    subscriptionAmount: 0,
    subscriptionStartDate: new Date().toISOString().split('T')[0],
    subscriptionDuration: 12, // months
    
    // Payment Details
    paymentMode: 'cash', // cash, check, bank_transfer
    paymentAmount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    receiptNumber: '',
    checkNumber: '',
    bankTransactionId: '',
    paymentNotes: ''
  });

  // Get dynamic subscription plans based on category and student range
  const subscriptionPlans = formData.category && formData.studentRange
    ? getSubscriptionPlans(formData.category, formData.studentRange)
    : [];

  // Get batch limit for selected category and student range
  const baseBatchLimit = formData.category && formData.studentRange
    ? getBaseBatchLimit(formData.category, formData.studentRange)
    : 20;

  // Update subscription amount when plan changes or category/range changes
  useEffect(() => {
    // Only update if we have valid category and student range
    if (formData.category && formData.studentRange && formData.subscriptionPlan) {
      const plans = getSubscriptionPlans(formData.category, formData.studentRange);
      const selectedPlan = plans.find(p => p.id === formData.subscriptionPlan);
      if (selectedPlan) {
        setFormData(prev => ({
          ...prev,
          subscriptionAmount: selectedPlan.price,
          subscriptionDuration: selectedPlan.duration,
          paymentAmount: selectedPlan.price
        }));
      }
    }
  }, [formData.subscriptionPlan, formData.category, formData.studentRange]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePlanChange = (planId) => {
    const plan = subscriptionPlans.find(p => p.id === planId);
    if (plan) {
      setFormData(prev => ({
        ...prev,
        subscriptionPlan: planId,
        subscriptionDuration: plan.duration,
        subscriptionAmount: plan.price,
        paymentAmount: plan.price
      }));
    }
  };

  const calculateEndDate = () => {
    const startDate = new Date(formData.subscriptionStartDate);
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + formData.subscriptionDuration);
    return endDate;
  };

  const validateStep1 = () => {
    if (!formData.coachingName || !formData.email || !formData.password) {
      toast.error('Please fill all required fields');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return false;
    }
    if (!formData.studentRange) {
      toast.error('Please select student range');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.subscriptionPlan) {
      toast.error('Please select a subscription plan');
      return false;
    }
    if (!formData.subscriptionAmount || formData.subscriptionAmount <= 0) {
      toast.error('Please select a valid subscription plan');
      return false;
    }
    if (subscriptionPlans.length === 0) {
      toast.error('Please go back and select category and student range');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.paymentAmount || formData.paymentAmount <= 0) {
      toast.error('Please enter payment amount');
      return false;
    }
    if (formData.paymentMode === 'check' && !formData.checkNumber) {
      toast.error('Please enter check number');
      return false;
    }
    if (formData.paymentMode === 'bank_transfer' && !formData.bankTransactionId) {
      toast.error('Please enter bank transaction ID');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep3()) {
      return;
    }

    setLoading(true);
    try {
      // Generate unique coaching ID if not provided
      const coachingId = formData.coachingId || 
        `INST${Date.now()}${Math.random().toString(36).substr(2, 4).toUpperCase()}`;

      // Step 1: Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const userId = userCredential.user.uid;

      // Step 2: Calculate subscription dates
      const startDate = new Date(formData.subscriptionStartDate);
      const endDate = calculateEndDate();

      // Step 3: Create user profile with subscription
      const profileData = {
        uid: userId,
        email: formData.email,
        role: 'coaching_admin',
        coachingName: formData.coachingName,
        coachingId: coachingId,
        contactPerson: formData.contactPerson,
        phone: formData.phone,
        category: formData.category,
        studentRange: formData.studentRange,
        address: formData.address,
        isActive: true,
        isLocked: false,
        subscription: {
          status: 'active',
          plan: formData.subscriptionPlan,
          amount: formData.subscriptionAmount,
          startDate: startDate,
          endDate: endDate,
          paymentMode: 'offline',
          createdBy: 'admin',
          createdAt: new Date()
        }
      };

      const profileResult = await createUserProfile(profileData);
      
      if (!profileResult.success) {
        throw new Error('Failed to create user profile: ' + (profileResult.error?.message || 'Unknown error'));
      }

      // Step 4: Record offline payment transaction
      const paymentData = {
        userId: userId,
        coachingId: coachingId,
        coachingName: formData.coachingName,
        paymentType: 'coaching_subscription_offline',
        paymentMode: formData.paymentMode,
        amount: formData.paymentAmount,
        currency: 'INR',
        status: 'completed',
        paymentDate: new Date(formData.paymentDate),
        receiptNumber: formData.receiptNumber || `RCP${Date.now()}`,
        checkNumber: formData.checkNumber || null,
        bankTransactionId: formData.bankTransactionId || null,
        notes: formData.paymentNotes,
        metadata: {
          subscriptionPlan: formData.subscriptionPlan,
          subscriptionDuration: formData.subscriptionDuration,
          subscriptionStartDate: startDate,
          subscriptionEndDate: endDate,
          createdBy: 'admin',
          offlinePayment: true
        }
      };

      const paymentResult = await paymentManagementService.savePaymentTransaction(paymentData);
      
      if (!paymentResult.success) {
        console.error('Payment recording failed:', paymentResult.error);
        // Don't fail the whole process if payment recording fails
      }

      toast.success('Institute account created successfully!');
      
      // Reset form
      setFormData({
        coachingName: '',
        coachingId: '',
        contactPerson: '',
        email: '',
        phone: '',
        password: '',
        address: { street: '', city: '', state: '', pincode: '' },
        category: '',
        studentRange: '',
        subscriptionPlan: 'yearly',
        subscriptionAmount: 0,
        subscriptionStartDate: new Date().toISOString().split('T')[0],
        subscriptionDuration: 12,
        paymentMode: 'cash',
        paymentAmount: 0,
        paymentDate: new Date().toISOString().split('T')[0],
        receiptNumber: '',
        checkNumber: '',
        bankTransactionId: '',
        paymentNotes: ''
      });
      setStep(1);

    } catch (error) {
      console.error('Error creating institute account:', error);
      toast.error(error.message || 'Failed to create institute account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="text-blue-600" />
            Create Institute Account (Offline Payment)
          </h1>
          <p className="text-gray-600 mt-2">
            Create a new institute account with cash, check, or bank transfer payment
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Institute Details' },
              { num: 2, label: 'Subscription Plan' },
              { num: 3, label: 'Payment Details' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      step >= s.num
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {step > s.num ? <CheckCircle className="w-6 h-6" /> : s.num}
                  </div>
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    {s.label}
                  </span>
                </div>
                {idx < 2 && (
                  <div
                    className={`flex-1 h-1 mx-4 ${
                      step > s.num ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* Step 1: Institute Details */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Institute Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institute Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.coachingName}
                      onChange={(e) => handleInputChange('coachingName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="ABC Coaching Institute"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institute ID (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.coachingId}
                      onChange={(e) => handleInputChange('coachingId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Auto-generated if empty"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      value={formData.contactPerson}
                      onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="institute@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="+91 9876543210"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={formData.address.street}
                        onChange={(e) => handleInputChange('address.street', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.address.city}
                        onChange={(e) => handleInputChange('address.city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={formData.address.state}
                        onChange={(e) => handleInputChange('address.state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pincode
                      </label>
                      <input
                        type="text"
                        value={formData.address.pincode}
                        onChange={(e) => handleInputChange('address.pincode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Category & Student Range</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category *
                      </label>
                      <select
                        required
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select category</option>
                        {CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Students *
                      </label>
                      <select
                        required
                        value={formData.studentRange}
                        onChange={(e) => handleInputChange('studentRange', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select student range</option>
                        {STUDENT_RANGES.map((range) => (
                          <option key={range.value} value={range.value}>
                            {range.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Pricing is calculated based on category and student range
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 2: Subscription Plan */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Subscription Plan
                </h2>

                {!formData.category || !formData.studentRange ? (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          Please go back to Step 1 and select your category and student range
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          Pricing is calculated based on these selections.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Batch Limit Information */}
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <BookOpen className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-blue-900 mb-2">
                            Included with Subscription:
                          </p>
                          <div className="text-sm text-blue-800 space-y-1">
                            <p>• <strong>{baseBatchLimit} batches</strong> included in base plan</p>
                            <p>• Need more? Purchase addon packs: <strong>₹{BATCH_ADDON_CONFIG.pricePerPack.toLocaleString()}/year</strong> for <strong>{BATCH_ADDON_CONFIG.batchesPerPack} batches</strong></p>
                            <p>• Maximum limit: <strong>{BATCH_ADDON_CONFIG.maxTotalBatches} batches</strong> (base + addons)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {subscriptionPlans.map((plan) => (
                        <div
                          key={plan.id}
                          onClick={() => handlePlanChange(plan.id)}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.subscriptionPlan === plan.id
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-300 hover:border-blue-400'
                          } ${plan.popular ? 'ring-2 ring-blue-300' : ''}`}
                        >
                          {plan.popular && (
                            <div className="text-xs font-bold text-blue-600 mb-2">
                              MOST POPULAR
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-lg">{plan.name}</h3>
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                formData.subscriptionPlan === plan.id
                                  ? 'border-blue-600 bg-blue-600'
                                  : 'border-gray-300'
                              }`}
                            >
                              {formData.subscriptionPlan === plan.id && (
                                <CheckCircle className="w-4 h-4 text-white" />
                              )}
                            </div>
                          </div>
                          <p className="text-2xl font-bold text-blue-600">
                            ₹{plan.price.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {plan.period}
                          </p>
                          {plan.savings && plan.savings > 0 && (
                            <p className="text-sm text-green-600 font-semibold mt-2">
                              Save ₹{plan.savings.toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.subscriptionStartDate}
                      onChange={(e) => handleInputChange('subscriptionStartDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date (Calculated)
                    </label>
                    <input
                      type="text"
                      value={calculateEndDate().toLocaleDateString('en-IN')}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>
                </div>

                {formData.category && formData.studentRange && subscriptionPlans.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-900">Subscription Summary</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Category: {CATEGORIES.find(c => c.value === formData.category)?.label}
                        </p>
                        <p className="text-sm text-blue-700">
                          Student Range: {STUDENT_RANGES.find(r => r.value === formData.studentRange)?.label}
                        </p>
                        <p className="text-sm text-blue-700">
                          Plan: {subscriptionPlans.find(p => p.id === formData.subscriptionPlan)?.name}
                        </p>
                        <p className="text-sm text-blue-700">
                          Duration: {formData.subscriptionDuration} month(s)
                        </p>
                        <p className="text-sm text-blue-700">
                          Amount: ₹{formData.subscriptionAmount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Payment Details */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Payment Details (Offline)
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Mode *
                    </label>
                    <select
                      value={formData.paymentMode}
                      onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="cash">Cash</option>
                      <option value="check">Check/Cheque</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Amount *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.paymentAmount}
                      onChange={(e) => handleInputChange('paymentAmount', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Receipt Number
                    </label>
                    <input
                      type="text"
                      value={formData.receiptNumber}
                      onChange={(e) => handleInputChange('receiptNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Auto-generated if empty"
                    />
                  </div>

                  {formData.paymentMode === 'check' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Check Number *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.checkNumber}
                        onChange={(e) => handleInputChange('checkNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Check number"
                      />
                    </div>
                  )}

                  {formData.paymentMode === 'bank_transfer' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bank Transaction ID *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.bankTransactionId}
                        onChange={(e) => handleInputChange('bankTransactionId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Transaction reference number"
                      />
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Notes
                    </label>
                    <textarea
                      value={formData.paymentNotes}
                      onChange={(e) => handleInputChange('paymentNotes', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Any additional notes about the payment..."
                    />
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">Payment Summary</p>
                      <p className="text-sm text-green-700 mt-1">
                        Mode: {formData.paymentMode.replace('_', ' ').toUpperCase()}
                      </p>
                      <p className="text-sm text-green-700">
                        Amount: ₹{formData.paymentAmount.toLocaleString()}
                      </p>
                      <p className="text-sm text-green-700">
                        Date: {new Date(formData.paymentDate).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <button
                type="button"
                onClick={handleBack}
                disabled={step === 1 || loading}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>

              <div className="flex gap-3">
                {step < 3 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Create Account
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstituteAccountCreation;
